import { lazy, Suspense } from "react";
import { useAuthContext } from "providers/Auth";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NavLink } from "./NavLink";
import { boomScreenLinks, boomReportLinks, Links } from "../constants";
import { Check } from "untitledui-js-base";
import { Menu } from "./Menu";
import { useProgressContext } from "providers/ProgressContext";
import { Loader } from "shared/components/Loader";
import { CsvUploadStatus } from "providers/ProgressContext/slices/UploadCsvSlice/types";
import { useCountMetaRentReportingController } from "views/rent-reporting/controllers/general-meta-controller";
import { cx } from "shared/utils";
import logoSquare from "shared/branding/logo-square.svg";
import { IconButton, IconButtonVariant } from "shared/components/IconButton";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { shouldShowReportingLinks } from "../utils";
import { PartnerFeatures } from "shared/types";
import { BadgeVariant, IconButtonSize } from "shared/components";
import {
  Cancel01Icon,
  FilterIcon,
  LaptopSettingsIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { GlobalLinkSection } from "./GlobalLinksSection";
import { BetaBadge } from "providers/Layout/components/BetaBadge";

const Notifications = lazy(() => import("./Notifications"));

interface AsideProps {
  isOpenFromMobile: boolean;
  isDefaultCollapsedPage: boolean;
  setIsOpenFromMobile: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Aside = ({
  isOpenFromMobile,
  setIsOpenFromMobile,
  isDefaultCollapsedPage
}: AsideProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { type, partner, permissions, partnerFeatures, auth } =
    useAuthContext();
  const { subscriptionsQueue, uploadCsv } = useProgressContext();
  const { needToReportCountMeta, pendingCountMeta } =
    useCountMetaRentReportingController();
  const hasChat = partnerFeatures.includes(PartnerFeatures.Chat);
  const analyticsAvailable = useFeatureFlagEnabled("analytics-tab");

  return (
    <aside
      className={cx(
        isOpenFromMobile
          ? "w-[240px] left-0"
          : "w-[80px] left-[-240px] md:left-0",
        "flex no-print transition-all top-0 absolute z-[14] h-screen flex-col border-r border-alpha-100 bg-white  md:hover:w-[240px]",
        isDefaultCollapsedPage
          ? ""
          : "lg:static lg:w-[240px] lg:min-w-[240px] lg:max-w-[240px]"
      )}
    >
      <div className="flex shrink-0">
        {isOpenFromMobile ? (
          <IconButton
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            className="!text-content-200 my-6 ml-6"
            onClick={() => setIsOpenFromMobile(false)}
          />
        ) : (
          <Link to="/" className="mb-8 ml-6 mt-4 block">
            <img src={logoSquare} className="h-[30px] w-[30px]" />
          </Link>
        )}
      </div>
      <div className="hide-scroll flex grow flex-col overflow-y-auto">
        <nav className="flex h-full flex-col relative">
          <GlobalLinkSection />

          {type.isBoomCRM ? (
            <div className="hideScroll mb-3 pl-4 pr-5">
              <p
                className={cx(
                  "mb-2 ml-3 hidden text-xs font-medium text-content-200",
                  isDefaultCollapsedPage ? "" : " lg:block"
                )}
              >
                BoomCRM
              </p>
              <NavLink to="/leads" HugeIcon={FilterIcon}>
                <div className="flex gap-2 items-center">
                  <p>Leads</p>
                  <BetaBadge
                    className="mix-blend-multiply"
                    variant={
                      location.pathname === "/leads"
                        ? BadgeVariant.PRIMARY
                        : BadgeVariant.BASE
                    }
                  />
                </div>
              </NavLink>
              <NavLink to="/listing-settings" HugeIcon={LaptopSettingsIcon}>
                <div className="flex gap-2 items-center">
                  <p>Listing settings</p>
                  <BetaBadge
                    className="mix-blend-multiply"
                    variant={
                      location.pathname === "/listing-settings"
                        ? BadgeVariant.PRIMARY
                        : BadgeVariant.BASE
                    }
                  />
                </div>
              </NavLink>
            </div>
          ) : null}
          {type.isBoomScreen ? (
            <div className="hideScroll mb-3 px-4">
              <p
                className={cx(
                  "mb-2 ml-3 hidden text-xs font-medium text-content-200",
                  isDefaultCollapsedPage ? "" : " lg:block"
                )}
              >
                BoomScreen
              </p>
              {boomScreenLinks
                .filter((link) => {
                  // Hide screening settings for PMs without any screening permissions
                  if (
                    type.isPropertyManager &&
                    link.name === Links.SCREENING_SETTINGS
                  )
                    // Show if they have either permission
                    return (
                      auth?.can_access_screening_templates ||
                      permissions.canManageDiscounts
                    );

                  if (!analyticsAvailable) return link.name !== "Analytics";

                  return true;
                })
                .map(({ to, name, Icon }) => {
                  const linkTo =
                    name === Links.SCREENING_SETTINGS &&
                    type.isPropertyManager &&
                    !auth?.can_access_screening_templates &&
                    permissions.canManageDiscounts
                      ? "/discounts"
                      : to;

                  return (
                    <NavLink
                      to={linkTo}
                      HugeIcon={Icon}
                      key={"boomscreen" + to + name}
                    >
                      {name}
                    </NavLink>
                  );
                })}
              {hasChat ? (
                <Suspense fallback={null}>
                  <Notifications />
                </Suspense>
              ) : null}
            </div>
          ) : null}
          {type.isRentReporting || type.isSDK ? (
            <div className="mb-6 border-solid border-alpha-40 px-4">
              <p
                className={cx(
                  "mb-2 ml-3 hidden text-xs font-medium text-content-200",
                  isDefaultCollapsedPage ? "" : " lg:block"
                )}
              >
                BoomReport
              </p>

              {boomReportLinks
                .filter(
                  ({ name, permission }) =>
                    shouldShowReportingLinks(name, type, partner) &&
                    permissions[permission as keyof typeof permissions]
                )
                .map(({ to, name, Icon, strokeWidth }) => (
                  <NavLink
                    to={to}
                    HugeIcon={Icon}
                    strokeWidth={strokeWidth}
                    key={"boomreport" + to + name}
                  >
                    <div className="flex gap-2 items-center">
                      {name}{" "}
                      {name === Links.RENT_REPORTING &&
                        ((type.isSDK
                          ? pendingCountMeta.data
                          : needToReportCountMeta.data) || 0) > 0 && (
                          <div className="flex items-center justify-center rounded bg-danger-500 text-xxs text-background-1000 px-2">
                            {type.isSDK
                              ? pendingCountMeta.data
                              : needToReportCountMeta.data}
                          </div>
                        )}
                    </div>
                  </NavLink>
                ))}
            </div>
          ) : null}
        </nav>
      </div>
      <div className="flex shrink-0 flex-col justify-end">
        {subscriptionsQueue.isSyncing ? (
          <div className="pl-4 pr-6">
            <div className="flex items-center gap-3 overflow-x-hidden p-3">
              <div className="shrink-0">
                <Loader size={20} />
              </div>
              <p className="text-xs text-content-500">
                Subscriptions queued: {subscriptionsQueue.amount}
              </p>
            </div>
          </div>
        ) : uploadCsv.status === CsvUploadStatus.Parsing ? (
          <Link to="/rent-reporting/upload">
            <div className="h-14 border-y border-alpha-100 bg-alpha-40 pl-4 pr-6">
              <div className="flex items-center gap-3 overflow-x-hidden p-3">
                <div className="mt-1 shrink-0 self-start">
                  <Loader size={20} />
                </div>
                <p className="text-xs text-content-500">
                  Analyzing rent reporting CSV file...
                </p>
              </div>
            </div>
          </Link>
        ) : uploadCsv.status === CsvUploadStatus.Parsed ? (
          <Link to="/rent-reporting/upload">
            <div className="h-14 border-y border-alpha-100 bg-success-50 pl-4 pr-6">
              <div className="flex items-center gap-3 overflow-x-hidden p-3">
                <div className="mt-1.5 flex h-5 w-5 shrink-0  items-center justify-center self-start rounded-full bg-success-600">
                  <Check size="14px" className="stroke-white stroke-[2px]" />
                </div>
                <p className="text-xs text-success-800">
                  Rent reporting CSV file analysis ready to review
                </p>
              </div>
            </div>
          </Link>
        ) : uploadCsv.status === CsvUploadStatus.Executing ? (
          <Link to="/rent-reporting/upload">
            <div className="h-14 border-y border-alpha-100 bg-alpha-40 pl-4 pr-6">
              <div className="flex items-center gap-3 overflow-x-hidden p-3">
                <div className="mt-1 shrink-0 self-start">
                  <Loader size={20} />
                </div>
                <p className="text-xs text-content-500">
                  Processing rent reporting CSV file...
                </p>
              </div>
            </div>
          </Link>
        ) : uploadCsv.status === CsvUploadStatus.Executed ? (
          <div
            className="h-14 border-y border-alpha-100 bg-success-50 pl-4 pr-6 cursor-pointer"
            onClick={() => {
              navigate("/rent-reporting/upload", {
                state: {
                  review: true
                }
              });
            }}
          >
            <div className="flex items-center gap-3 overflow-x-hidden p-3">
              <div className="mt-1.5 flex h-5 w-5 shrink-0  items-center justify-center self-start rounded-full bg-success-600">
                <Check size="14px" className="stroke-white stroke-[2px]" />
              </div>
              <p className="text-xs text-success-800">
                Rent reporting CSV file processed successfully
              </p>
            </div>
          </div>
        ) : null}

        <Menu />
      </div>
    </aside>
  );
};
